import type { ReactNode } from 'react';

export interface GameModeActionProps {
  /** One of the mode glyphs (`gameModeSolo`/`gameModeVersus`/`gameModeMultiplayer`) resolved via `assetUrl`. */
  icon: string;
  /**
   * The glyph's rendered box *and* its negative bottom margin — the offset that
   * seats it into the button. Both are supplied per mode rather than fixed
   * here, because each master carries a different amount of transparent canvas
   * below its artwork, so a shared offset seats the three circles at three
   * different depths (see `MODE_ICON_CLASS` in `GameCatalogCard`).
   */
  iconClassName: string;
  /** The launch control for this mode — a `GameButton`/`GameActionLink`, so this component owns only layout. */
  children: ReactNode;
  className?: string;
}

/**
 * One launch mode on a `GameCatalogCard` (approved Games-page mockup "ICON
 * PLACEMENT"): the mode's illustrated glyph seated directly on top of its
 * launch button, the two reading as a single composed control.
 *
 * The glyph overlaps the button's upper edge rather than standing clear of it.
 * A glyph, a label and a button stacked with gaps read as three separate
 * things and cost three rows of height; overlapping them makes one object and
 * gives the height back to the card. The mode's *name* is deliberately not
 * rendered here at all — the metadata line above already says "Solo · 2
 * players", and the button itself says "Play Solo", so a "SOLO" caption
 * between them was the same word three times.
 *
 * The glyph is decorative and inert: the button's own label is the accessible
 * name, so the image is `aria-hidden` and `pointer-events-none`, which is what
 * keeps the overlapping region a live part of the button rather than a dead
 * strip over it.
 */
export function GameModeAction({
  icon,
  iconClassName,
  children,
  className = '',
}: GameModeActionProps) {
  return (
    <div className={`relative flex w-full flex-col items-center ${className}`}>
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        width={256}
        height={256}
        className={`pointer-events-none relative z-[2] shrink-0 select-none object-contain [filter:drop-shadow(0_3px_7px_rgb(75_46_34/0.38))] ${iconClassName}`}
        draggable={false}
      />
      {/* The control fills the group's width so two modes on one card present
          a matched pair of buttons rather than a wide one beside a narrow one.
          Width is capped by the caller, which knows how many modes share the
          row. */}
      <div className="relative z-[1] w-full [&>*]:w-full">{children}</div>
    </div>
  );
}
